import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import api, { apiError } from "../../lib/api";
import { fmtDate, fmtMoney, INVOICE_STATUS } from "../../lib/labels";
import { Chip, EmptyState, PageHeader, ToneChip } from "../../components/common";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

function IntakeDialog({ open, onClose, onDone }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({ supplier_id: "", invoice_number: "", issue_date: "", due_date: "", subtotal: "", taxes: "", total: "", po_id: "", budget_reference: "", iban: "" });
  const [busy, setBusy] = useState(false);
  const { data: suppliers } = useQuery({ queryKey: ["fin-suppliers-all"], queryFn: async () => (await api.get("/finanzas/suppliers")).data, staleTime: 60_000 });
  const { data: pos } = useQuery({ queryKey: ["fin-pos-mini"], queryFn: async () => (await api.get("/finanzas/purchase-orders")).data, staleTime: 60_000 });
  const { data: budget } = useQuery({ queryKey: ["fin-budget-refs"], queryFn: async () => (await api.get("/finanzas/budget")).data, staleTime: 60_000 });

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        subtotal: Number(form.subtotal), taxes: form.taxes ? Number(form.taxes) : undefined, total: Number(form.total),
        po_id: form.po_id || undefined, budget_reference: form.budget_reference || undefined, iban: form.iban || undefined,
      };
      const r = await api.post("/finanzas/invoices", payload);
      const inv = r.data;
      if (inv.duplicate_status !== "no_duplicado") {
        toast.warning("Posible duplicado detectado", { description: "Factura bloqueada y excepción crítica creada. Pago bloqueado." });
      } else {
        toast.success(`Factura ${inv.invoice_number} recibida — extracción IA pendiente de validación humana`);
      }
      onDone(inv);
      navigate(`/finanzas/facturas/${inv.id}`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xl" data-testid="invoice-intake-dialog">
        <DialogHeader><DialogTitle>Registrar factura de proveedor</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Proveedor *</Label>
              <Select value={form.supplier_id} onValueChange={(v) => setForm({ ...form, supplier_id: v })}>
                <SelectTrigger data-testid="inv-supplier"><SelectValue placeholder="Selecciona…" /></SelectTrigger>
                <SelectContent>{(suppliers || []).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Número de factura *</Label><Input value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} data-testid="inv-number" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Fecha de emisión</Label><Input type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} data-testid="inv-issue-date" /></div>
            <div><Label>Vencimiento</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="inv-due-date" /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Base (€) *</Label><Input type="number" value={form.subtotal} onChange={(e) => setForm({ ...form, subtotal: e.target.value })} data-testid="inv-subtotal" /></div>
            <div><Label>IVA (€)</Label><Input type="number" value={form.taxes} onChange={(e) => setForm({ ...form, taxes: e.target.value })} data-testid="inv-taxes" /></div>
            <div><Label>Total (€) *</Label><Input type="number" value={form.total} onChange={(e) => setForm({ ...form, total: e.target.value })} data-testid="inv-total" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Orden de compra</Label>
              <Select value={form.po_id || "none"} onValueChange={(v) => setForm({ ...form, po_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="inv-po"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin orden</SelectItem>
                  {(pos || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.po_number} — {p.supplier_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Partida presupuestaria</Label>
              <Select value={form.budget_reference || "none"} onValueChange={(v) => setForm({ ...form, budget_reference: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="inv-budget"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin partida</SelectItem>
                  {(budget?.references || []).map((r) => <SelectItem key={r.code} value={r.code}>{r.code}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <p className="rounded-sm border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
            El sistema detecta duplicados (mismo número, fecha+importe o número casi idéntico) y bloquea el pago hasta resolverlos.
          </p>
          <Button className="w-full" onClick={submit} disabled={busy || !form.supplier_id || !form.invoice_number.trim() || !Number(form.total)} data-testid="inv-intake-submit">
            {busy ? "Procesando…" : "Registrar y extraer (IA demo)"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Facturas() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const { data: items } = useQuery({
    queryKey: ["fin-invoices", status, q],
    queryFn: async () => (await api.get("/finanzas/invoices", { params: { status: status || undefined, q: q || undefined } })).data,
  });

  return (
    <div data-testid="facturas-page">
      <PageHeader
        title="Facturas de proveedores"
        subtitle="Recepción, extracción IA validada por humanos, matching (PO + conformidad + presupuesto) y aprobación por matriz."
        actions={<Button onClick={() => setOpen(true)} data-testid="new-invoice-button"><Plus className="mr-1 h-4 w-4" /> Registrar factura</Button>}
      />
      <div className="mb-4 flex flex-wrap gap-2">
        <Input placeholder="Buscar por número, proveedor, proyecto…" value={q} onChange={(e) => setQ(e.target.value)} className="w-72" data-testid="inv-search" />
        <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
          <SelectTrigger className="w-60" data-testid="inv-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            {Object.entries(INVOICE_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {items && items.length === 0 && <EmptyState title="Sin facturas" description="Registra la primera factura de proveedor." />}
      {items?.length > 0 && (
        <div className="ts-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow><TableHead>Factura</TableHead><TableHead>Proveedor</TableHead><TableHead>Vencimiento</TableHead><TableHead>Matching</TableHead><TableHead>Estado</TableHead><TableHead className="text-right">Total</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {items.map((inv) => (
                <TableRow key={inv.id} data-testid={`inv-row-${inv.id}`} className="cursor-pointer" onClick={() => navigate(`/finanzas/facturas/${inv.id}`)}>
                  <TableCell className="font-mono text-xs font-medium">{inv.invoice_number}</TableCell>
                  <TableCell className="text-sm">{inv.supplier_name}</TableCell>
                  <TableCell className="text-xs">{fmtDate(inv.due_date)}</TableCell>
                  <TableCell>
                    {inv.duplicate_status !== "no_duplicado"
                      ? <ToneChip label="Posible duplicado" tone="danger" testid={`inv-dup-${inv.id}`} />
                      : inv.controls?.length
                        ? <span className="text-xs text-slate-500">{inv.controls.filter((c) => c.result === "superado").length}/{inv.controls.length} controles</span>
                        : <span className="text-xs text-slate-400">sin matching</span>}
                  </TableCell>
                  <TableCell><Chip map={INVOICE_STATUS} value={inv.status} testid={`inv-status-${inv.id}`} /></TableCell>
                  <TableCell className="text-right font-mono text-sm">{fmtMoney(inv.total)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <IntakeDialog open={open} onClose={() => setOpen(false)} onDone={() => { setOpen(false); qc.invalidateQueries({ queryKey: ["fin-invoices"] }); }} />
    </div>
  );
}
